import { PageHeader, Pagination } from '@/shared/components';
import { BookingsQuarantineHint } from '@/features/booking-quarantine';
import { BookingAgenda } from './agenda/BookingAgenda';
import {
  ApartmentsUnavailable,
  BookingsListEmpty,
  BookingsListError,
  BookingsListLoading,
} from './agenda/BookingsListState';
import { BookingsToolbar } from './agenda/BookingsToolbar';
import { NewManualBookingButton } from './agenda/NewManualBookingButton';
import { bookingsSubline, bookingsTitle, STATUS_TABS } from './agenda/bookingsHeader';
import { useBookingsList } from './agenda/useBookingsList';
import { PageSizeSelector } from './components/PageSizeSelector';

/**
 * „Aktuell & kommend“ (Board „Buchungen“): ab heute, wer im Haus ist, heute an- oder abreist und
 * wer noch kommt, als Tagesliste. „Vergangene zeigen“ hebt den Filter „Abreise ab heute“ auf.
 * Heute ist der Tag in der Zeitzone des gewählten Objekts, ohne Objekt die des Property-Standards.
 */
export function BookingsPage() {
  const {
    canSeeGuests,
    today,
    filters,
    sort,
    pageSize,
    page,
    query,
    apply,
    setFilters,
    list,
    properties,
    propertyNames,
    sections,
    filtered,
    reset,
  } = useBookingsList();
  const data = list.data;
  return (
    <>
      <PageHeader
        className="pb-[84px] md:pb-[84px]"
        title={bookingsTitle(filters.showPast, today)}
        description={
          data
            ? bookingsSubline(data, filters.showPast, filtered)
            : list.isError
              ? 'Die Liste ist gerade nicht geladen.'
              : 'Wird geladen…'
        }
        actions={
          <NewManualBookingButton
            properties={properties.data?.items ?? []}
            status={properties.isError ? 'error' : properties.isPending ? 'loading' : 'ready'}
          />
        }
        filterTabs={STATUS_TABS.map(([status, label]) => ({
          label,
          active: filters.status === status,
          onSelect: () => setFilters({ ...filters, status }),
        }))}
        filterTabsLabel="Status"
      />
      <div className="relative z-10 -mt-[84px] flex flex-col gap-5 px-2 md:px-5">
        <BookingsToolbar
          filters={filters}
          onFiltersChange={setFilters}
          query={query}
          onQueryChange={(next) => apply({ query: next, page: 1 })}
          sort={sort}
          onSortChange={(next) => apply({ sort: next, page: 1 })}
          onReset={reset}
          canSeeGuests={canSeeGuests}
          properties={properties.data?.items ?? []}
        />
        {properties.isError && (
          <ApartmentsUnavailable
            onRetry={() => void properties.refetch()}
            retrying={properties.isFetching}
          />
        )}
        <BookingsQuarantineHint />
        {list.isError && !data ? (
          <BookingsListError
            error={list.error}
            onRetry={() => void list.refetch()}
            retrying={list.isFetching}
            onReset={filtered || filters.showPast ? reset : undefined}
          />
        ) : !data ? (
          <BookingsListLoading />
        ) : data.items.length === 0 ? (
          <BookingsListEmpty
            filtered={filtered}
            showPast={filters.showPast}
            onReset={reset}
            onShowPast={() => setFilters({ ...filters, showPast: true })}
          />
        ) : (
          <>
            {list.isError && (
              <BookingsListError
                error={list.error}
                onRetry={() => void list.refetch()}
                retrying={list.isFetching}
              />
            )}
            <BookingAgenda
              sections={sections}
              propertyNames={propertyNames}
              canSeeGuests={canSeeGuests}
              today={today}
            />
            <div className="flex flex-wrap items-center gap-3 md:pl-[120px]">
              <PageSizeSelector
                value={pageSize}
                onChange={(next) => apply({ pageSize: next, page: 1 })}
              />
              <div className="ml-auto">
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  total={data.total}
                  onPageChange={(next) => apply({ page: next })}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
